// ============================================================
// PERF OVERLAY — Fase 12.4
// Overlay técnico de performance ativável em runtime
// Exibe: FPS, heap, channels, intervals, renders/s, stalls…
//
// ATIVAÇÃO:
//   ?perf=1       query param
//   localStorage['canteiro:perf'] = '1'
//   Ctrl+Shift+P
// ============================================================
import { useEffect, useRef, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getWatchdogStatus } from '@/core/tvWatchdog'
import { getRegistryCount } from '@/core/leak/intervalRegistry'
import { getChannelSnapshot } from '@/core/leak/channelMonitor'
import { getLastLeakReport } from '@/core/leak/memoryLeakDetector'

// ─── Activation check ─────────────────────────────────────
function isPerfEnabled(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  if (params.get('perf') === '1') return true
  try {
    if (localStorage.getItem('canteiro:perf') === '1') return true
  } catch { /* */ }
  return false
}

// ─── Metrics collector ────────────────────────────────────
interface PerfSnapshot {
  fps:           number
  heapMB:        number
  heapGrowthMB:  number
  openChannels:  number
  zombieChannels: number
  intervals:     number
  timeouts:      number
  rafs:          number
  stallCount:    number
  sinceActivityMs: number
  leakRisk:      string
  renderCount:   number
  rendersPerSec: number
}

function collectSnapshot(prevHeap: number, renderCount: number, rendersPerSec: number): PerfSnapshot {
  let heapMB = 0
  if (typeof performance !== 'undefined' && (performance as any).memory) {
    heapMB = Math.round((performance as any).memory.usedJSHeapSize / 1_048_576)
  }
  const watchdog = getWatchdogStatus()
  const counts   = getRegistryCount()
  const channels = getChannelSnapshot()
  const leak     = getLastLeakReport()

  return {
    fps:             0,  // set by RAF loop
    heapMB,
    heapGrowthMB:    heapMB - prevHeap,
    openChannels:    channels.openCount,
    zombieChannels:  channels.zombies.length,
    intervals:       counts.interval,
    timeouts:        counts.timeout,
    rafs:            counts.raf,
    stallCount:      watchdog.stallCount,
    sinceActivityMs: watchdog.sinceActivityMs,
    leakRisk:        leak?.overallRisk ?? 'info',
    renderCount,
    rendersPerSec,
  }
}

// ─── Component ────────────────────────────────────────────
export function PerfOverlay() {
  const [visible, setVisible]     = useState(isPerfEnabled)
  const [snap,    setSnap]        = useState<PerfSnapshot | null>(null)

  const fpsRef          = useRef(0)
  const frameCountRef   = useRef(0)
  const lastFpsTs       = useRef(Date.now())
  const renderCountRef  = useRef(0)
  const rpsBucketRef    = useRef(0)
  const rpsRef          = useRef(0)
  const lastHeapRef     = useRef(0)
  const rafRef          = useRef<number>(0)

  // RAF-based FPS counter
  useEffect(() => {
    if (!visible) return
    let last = performance.now()

    function frame(now: number) {
      const delta = now - last
      last = now
      if (delta > 0) fpsRef.current = Math.round(1000 / delta)
      frameCountRef.current++
      rafRef.current = requestAnimationFrame(frame)
    }
    rafRef.current = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(rafRef.current)
  }, [visible])

  // Snapshot every 500ms
  useEffect(() => {
    if (!visible) return
    const t = setInterval(() => {
      const now = Date.now()
      const fpsSample = Math.min(fpsRef.current, 120)

      // RPS (renders/s)
      rpsBucketRef.current++
      if (now - lastFpsTs.current >= 1_000) {
        rpsRef.current   = rpsBucketRef.current
        rpsBucketRef.current = 0
        lastFpsTs.current = now
      }

      const s = collectSnapshot(lastHeapRef.current, renderCountRef.current, rpsRef.current)
      s.fps = fpsSample
      lastHeapRef.current = s.heapMB
      setSnap(s)
    }, 500)
    return () => clearInterval(t)
  }, [visible])

  // Keyboard shortcut Ctrl+Shift+P
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        setVisible(v => {
          const next = !v
          try { localStorage.setItem('canteiro:perf', next ? '1' : '0') } catch { /* */ }
          return next
        })
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  if (!visible || !snap) return null

  const row = (label: string, value: string | number, warn?: boolean, crit?: boolean) => (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '2px 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      <span style={{ color: '#64748b', fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>{label}</span>
      <span style={{
        fontSize: 11, fontWeight: 800, fontFamily: 'monospace',
        color: crit ? '#f87171' : warn ? '#fbbf24' : '#94a3b8',
      }}>{String(value)}</span>
    </div>
  )

  const fmtMs = (ms: number) => ms > 1000 ? `${(ms/1000).toFixed(1)}s` : `${ms}ms`

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        style={{
          position: 'fixed',
          top: 8,
          right: 8,
          zIndex: 9999,
          background: 'rgba(7,11,26,0.92)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 10,
          padding: '10px 14px',
          minWidth: 180,
          backdropFilter: 'blur(8px)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}
        drag
        dragMomentum={false}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 2, color: '#F59E0B' }}>PERF OVERLAY</span>
          <button
            onClick={() => setVisible(false)}
            style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
          >×</button>
        </div>

        {row('FPS',          snap.fps,             snap.fps < 30,  snap.fps < 15)}
        {row('HEAP',         `${snap.heapMB}MB`,    snap.heapMB > 250, snap.heapMB > 350)}
        {row('HEAP Δ',       `${snap.heapGrowthMB > 0 ? '+' : ''}${snap.heapGrowthMB}MB`, snap.heapGrowthMB > 20, snap.heapGrowthMB > 50)}
        {row('CHANNELS',     snap.openChannels,    snap.openChannels > 8, snap.openChannels > 15)}
        {row('ZOMBIES',      snap.zombieChannels,  snap.zombieChannels > 0, snap.zombieChannels > 3)}
        {row('INTERVALS',    snap.intervals,       snap.intervals > 15,   snap.intervals > 30)}
        {row('TIMEOUTS',     snap.timeouts,        snap.timeouts  > 20,   snap.timeouts  > 40)}
        {row('RAFs',         snap.rafs,            snap.rafs > 5,  snap.rafs > 10)}
        {row('STALLS',       snap.stallCount,      snap.stallCount > 0, snap.stallCount > 3)}
        {row('LAST ACT',     fmtMs(snap.sinceActivityMs), snap.sinceActivityMs > 15_000, snap.sinceActivityMs > 25_000)}
        {row('LEAK RISK',    snap.leakRisk.toUpperCase(), snap.leakRisk === 'warning', snap.leakRisk === 'critical')}

        <div style={{ fontSize: 9, color: '#1e293b', marginTop: 8, textAlign: 'right' }}>
          Ctrl+Shift+P to toggle
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
